import {Navigate} from 'react-router-dom';
import {Spinner} from '@kroll-resolver-crisp/ui-components-react';
import {
  useConfigList,
  useUsers,
  useClassificationConfig,
  useClassificationConfigs
} from '../api/hooks';
import {useAuth} from '../context/useAuth';
import {ConfigAdmin} from '../components/admin/ConfigAdmin';
import {ClassificationVisibilityAdmin} from '../components/admin/ClassificationVisibilityAdmin';
import {isPolicyViolationType} from '@/lib/classificationType';
import {type AdminSectionSlug, adminSectionPath} from '@/lib/adminSections';

// Two roles land here. analyst_admin can hide/unhide taxonomy entries (TaxonomyConfigsController is
// BroadView) but every other settings section reads AdminConfigController, which is admin-only - so
// App.tsx routes it to Classifications alone, which it gets as the toggle-only view rather than the
// full editor. Split into two components so each role only ever issues the queries it is allowed to
// make.
export function AdminPage({section}: {readonly section: AdminSectionSlug}) {
  const {user} = useAuth();

  return user?.role === 'admin' ? (
    <SettingsAdmin section={section} currentUserEmail={user.username ?? ''} />
  ) : (
    <ClassificationVisibility />
  );
}

// /admin is no longer a page of its own: it opens the first section the role can see, so old links
// and bookmarks to it still land somewhere useful.
export function AdminIndexRedirect() {
  const {user} = useAuth();
  return (
    <Navigate replace to={adminSectionPath(user?.role === 'admin' ? 'users' : 'classifications')} />
  );
}

function ClassificationVisibility() {
  const {data, isLoading} = useClassificationConfigs();

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 p-6 text-xs" role="status">
        <Spinner /> Loading…
      </div>
    );
  }

  return <ClassificationVisibilityAdmin initialConfigs={data ?? []} />;
}

function SettingsAdmin({
  section,
  currentUserEmail
}: {
  readonly section: AdminSectionSlug;
  readonly currentUserEmail: string;
}) {
  const languages = useConfigList('language');
  const topics = useConfigList('topic');
  const locations = useConfigList('location');
  const coreRisks = useConfigList('core_risk');
  const users = useUsers();
  const classifications = useClassificationConfig();

  // Deliberately NOT gating on hopper configs here (there is no `hoppers.isLoading` in this list):
  // ConfigAdmin fetches hopper configs itself (ConfigAdmin.tsx, `useHopperConfigs()`) and already
  // tolerates loading/error by defaulting to `[]`, the same as its own Prompts/Classifications/
  // Feedback data. A second `useHopperConfigs()` observer here - previously present, gating this
  // page's `loading` boolean even though its `data` was never read - raced with ConfigAdmin's own:
  // once every query here settled into a (no-backend) error state, `loading` would briefly go
  // false, mounting ConfigAdmin; ConfigAdmin's fresh `useHopperConfigs()` observer then triggered
  // TanStack Query's default refetch-on-mount against the already-errored, dataless query, flipping
  // `isLoading` back to true here too - unmounting ConfigAdmin and repeating forever. Every other
  // config type avoids this because AdminPage fetches it exactly once and passes it down as a
  // prop, with no second live subscription to race against.
  const loading =
    languages.isLoading ||
    topics.isLoading ||
    locations.isLoading ||
    coreRisks.isLoading ||
    users.isLoading ||
    classifications.isLoading;
  if (loading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 p-6 text-xs" role="status">
        <Spinner /> Loading…
      </div>
    );
  }

  const verticals = (classifications.data ?? [])
    .filter((c) => isPolicyViolationType(c.type))
    .map((c) => c.name);

  return (
    <ConfigAdmin
      section={section}
      initialLanguages={languages.data ?? []}
      initialTopics={topics.data ?? []}
      initialLocations={locations.data ?? []}
      initialCoreRisks={coreRisks.data ?? []}
      initialUsers={users.data ?? []}
      currentUserEmail={currentUserEmail}
      verticals={verticals}
    />
  );
}
